import { expVal } from '@confluence/feature-experiments';
import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import {
	stringifyPersonalizationAttributes,
	type PersonalizationAttribute,
	type StringifiedPersonalizationAttribute,
} from '@confluence/legacy-schema-bridge/entry-points/stringifyPersonalizationAttributes.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	ExcludeNewConfluenceActiveUserQuery as ExcludeNewConfluenceActiveUserQueryData,
	ExcludeNewConfluenceActiveUserQueryVariables,
} from './__types__/ExcludeNewConfluenceActiveUserQuery.ts';
import { useSiteUserTraits, type UseSiteUserTraitsOptions } from './useSiteUserTraits.ts';

export type UseExperimentalSiteUserPersonalizationParams = {
	variables: ExcludeNewConfluenceActiveUserQueryVariables;
	skip?: boolean;
	fetchPolicy?: UseSiteUserTraitsOptions['fetchPolicy'];
};

export type UseExperimentalSiteUserPersonalizationResult = {
	data:
		| {
				experimentalSiteUserPersonalization: {
					attributes: StringifiedPersonalizationAttribute[] | null;
				};
		  }
		| undefined;
	loading: boolean;
	error: Error | ApolloErrorInterop | undefined;
};

export const useExperimentalSiteUserPersonalization = ({
	variables,
	skip,
	fetchPolicy,
}: UseExperimentalSiteUserPersonalizationParams): UseExperimentalSiteUserPersonalizationResult => {
	const legacyResult = useExperimentalQuery<
		ExcludeNewConfluenceActiveUserQueryData,
		ExcludeNewConfluenceActiveUserQueryVariables,
		{ attributes: PersonalizationAttribute[] | null }
	>({
		url: ({ cloudId, userId }) =>
			`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${userId}`,
		method: 'GET',
		variables,
		parseResult: (json): ExcludeNewConfluenceActiveUserQueryData => ({
			experimentalSiteUserPersonalization: {
				attributes: stringifyPersonalizationAttributes(json.attributes),
			},
		}),
		skip: skip || expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false),
	});

	const traitsResult = useSiteUserTraits({
		skip: skip || !expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false),
		fetchPolicy,
	});

	if (!expVal('cc_ce_mau_deprecated_tap_wrappers_migration', 'isEnabled', false)) {
		return {
			data: legacyResult.data as UseExperimentalSiteUserPersonalizationResult['data'],
			loading: legacyResult.loading,
			error: legacyResult.error ?? undefined,
		};
	}

	// Exp on: adapt traits result into legacy data/loading/error shape
	const adaptedData =
		traitsResult.data !== null
			? {
					experimentalSiteUserPersonalization: {
						attributes: stringifyPersonalizationAttributes(traitsResult.data.attributes),
					},
				}
			: undefined;

	return {
		data: adaptedData,
		loading: traitsResult.loading,
		error: traitsResult.error ?? undefined,
	};
};
